import React from "react";

const iconMap = {
  Brain: "🧠",
  Network: "🔗",
  Shield: "🛡",
  Lightbulb: "💡",
  MessageSquare: "💬",
  TrendingUp: "📈",
  Gem: "💎",
  Zap: "⚡",
  Target: "🎯",
  Map: "🗺",
  ListChecks: "✅",
  Compass: "🧭",
  Award: "🏆",
  Bot: "🤖",
  Footprints: "👣",
  BookOpen: "📖",
  Wrench: "🔧",
  Share2: "📢",
  Users: "👥",
  CheckCircle: "✔",
  DollarSign: "💵",
  Calendar: "📅",
  Clock: "⏱",
  FileText: "📄",
};

const getIcon = (name) => iconMap?.[name] || "•";

const Divider = () => (
  <div style={{ borderTop: "1px solid #ccc", margin: "18px 0" }} />
);

const SectionTitle = ({ icon, label, title }) => (
  <div style={{ marginBottom: 10 }}>
    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#888" }}>
      {label}
    </span>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
      <span style={{ fontSize: 18 }}>{getIcon(icon)}</span>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#1a1a2e" }}>{title}</h2>
    </div>
  </div>
);

const BlueprintPrintLayout = ({ formData, blueprint }) => {
  const name = formData?.name || "Professional";
  const profession = formData?.profession || "your field";

  const hiddenValueAssets = blueprint?.hiddenValueAssets || [];
  const aiOpportunities = blueprint?.aiOpportunities || [];
  const recommendedPath = blueprint?.recommendedPath || {};
  const launchWeeks = blueprint?.launchWeeks || [];
  const firstThreeSteps = blueprint?.firstThreeSteps || [];

  const weekIcons = ["BookOpen", "Wrench", "Share2", "Users"];

  const pillars = [
    {
      icon: "Award",
      title: "Your Experience Has Real Value",
      body: "Decades of professional work have given you pattern recognition, judgment, and domain knowledge that cannot be replicated by someone starting fresh. These are not soft advantages — they are hard economic assets.",
    },
    {
      icon: "Bot",
      title: "AI Is Leverage, Not a Replacement",
      body: "AI tools amplify what you already know. They handle the repetitive, the mechanical, the time-consuming. You provide the wisdom, the context, and the human judgment that makes the output actually useful.",
    },
    {
      icon: "Footprints",
      title: "Small Execution Beats Overthinking",
      body: "The professionals who succeed in this transition are not the ones who plan the most — they are the ones who start the smallest viable version and refine from there. One client. One offer. One week at a time.",
    },
  ];

  return (
    <div
      className="blueprint-print-only"
      style={{
        fontFamily: "Georgia, 'Times New Roman', serif",
        color: "#1a1a2e",
        fontSize: 12,
        lineHeight: 1.6,
        padding: "24px 32px",
        maxWidth: 720,
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, color: "#888", marginBottom: 4 }}>
          Personalized Blueprint
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 6px 0", color: "#1a1a2e" }}>
          {name}&apos;s QuantuMe Prospectus AI Income Blueprint
        </h1>
        <p style={{ fontSize: 12, color: "#555", margin: 0 }}>
          Tailored for your background in <strong>{profession}</strong> — a strategic roadmap to monetize your expertise in the AI-powered economy.
        </p>
      </div>
      {/* Snapshot */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#888", marginBottom: 6 }}>
          Your Prospectus Snapshot
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: 700, color: "#283ba3", paddingBottom: 4, width: "40%", verticalAlign: "top" }}>Primary Strength Pattern</td>
              <td style={{ paddingBottom: 4, verticalAlign: "top" }}>{blueprint?.hiddenValueAssets?.[0]?.title || "—"}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 700, color: "#283ba3", paddingBottom: 4, verticalAlign: "top" }}>Best Income Model</td>
              <td style={{ paddingBottom: 4, verticalAlign: "top" }}>{blueprint?.aiOpportunities?.[0]?.incomeModel || "—"}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 700, color: "#283ba3", verticalAlign: "top" }}>Fastest Starting Path</td>
              <td style={{ verticalAlign: "top" }}>{blueprint?.recommendedPath?.title || "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <Divider />
      {/* Section 1: Hidden Value Assets */}
      <SectionTitle icon="Gem" label="Section 1" title="Your Hidden Value Assets" />
      <p style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        After decades of professional experience, you have accumulated assets that most people overlook. Here is what makes you uniquely valuable in today&apos;s economy.
      </p>
      {hiddenValueAssets?.map((item, idx) => (
        <div key={idx} style={{ marginBottom: 8, paddingLeft: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{getIcon(item?.icon)}</span>
            <div>
              <strong style={{ fontSize: 12, color: "#1a1a2e" }}>{item?.title}</strong>
              <p style={{ margin: "2px 0 0 0", fontSize: 11, color: "#444", lineHeight: 1.5 }}>{item?.description}</p>
            </div>
          </div>
        </div>
      ))}
      <p style={{ fontSize: 11, fontStyle: "italic", color: "#283ba3", marginTop: 8 }}>
        Why this is economically valuable: Your pattern recognition and domain expertise are precisely what AI tools need human guidance to apply correctly. You are not competing with AI — you are directing it.
      </p>
      <Divider />
      {/* Section 2: AI Amplification Opportunities */}
      <SectionTitle icon="Zap" label="Section 2" title="AI Amplification Opportunities" />
      <p style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        Three specific ways AI tools can multiply your existing expertise — turning what you already know into scalable income streams.
      </p>
      {aiOpportunities?.map((opp, idx) => (
        <div key={idx} style={{ marginBottom: 12, paddingLeft: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#283ba3" }}>{idx + 1}.</span>
            <strong style={{ fontSize: 13, color: "#1a1a2e" }}>{opp?.title}</strong>
          </div>
          <div style={{ paddingLeft: 20 }}>
            <p style={{ margin: "0 0 3px 0", fontSize: 11, color: "#444" }}>
              <strong>Income Model:</strong> {opp?.incomeModel}
            </p>
            <p style={{ margin: "0 0 3px 0", fontSize: 11, color: "#444" }}>
              <strong>Who It Serves:</strong> {opp?.whoItServes}
            </p>
            <p style={{ margin: "0 0 3px 0", fontSize: 11, color: "#444" }}>
              <strong>How AI Helps:</strong> {opp?.howAIHelps}
            </p>
            <p style={{ margin: 0, fontSize: 11, color: "#444" }}>
              <strong>Estimated Monthly Income:</strong> {opp?.estimatedIncome}
            </p>
          </div>
        </div>
      ))}
      <Divider />
      {/* Section 3: Recommended Starting Path */}
      <SectionTitle icon="Target" label="Section 3" title="Recommended Starting Path" />
      <p style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 4 }}>{recommendedPath?.title}</p>
      <p style={{ fontSize: 12, color: "#444", marginBottom: 8 }}>{recommendedPath?.description}</p>
      <div style={{ paddingLeft: 4 }}>
        <p style={{ margin: "0 0 5px 0", fontSize: 11, color: "#444" }}>
          <strong>✔ Why It Fits You:</strong> {recommendedPath?.whyItFits}
        </p>
        <p style={{ margin: "0 0 5px 0", fontSize: 11, color: "#444" }}>
          <strong>💵 How It Generates Income:</strong> {recommendedPath?.howItGeneratesIncome}
        </p>
        <p style={{ margin: 0, fontSize: 11, color: "#444" }}>
          <strong>📅 30-Day Feasibility:</strong> {recommendedPath?.feasibility}
        </p>
      </div>
      <Divider />
      {/* Section 4: 30-Day Launch Plan */}
      <SectionTitle icon="Map" label="Section 4" title="30-Day Launch Plan" />
      <p style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        A practical, week-by-week roadmap. Each step builds on the last. No overwhelm — just clear, sequential actions.
      </p>
      {launchWeeks?.map((week, idx) => (
        <div key={idx} style={{ marginBottom: 10, paddingLeft: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 14 }}>{getIcon(weekIcons?.[idx])}</span>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#888" }}>Week {idx + 1}</span>
              <strong style={{ display: "block", fontSize: 12, color: "#1a1a2e" }}>{week?.theme}</strong>
            </div>
          </div>
          <ul style={{ margin: "0 0 0 28px", padding: 0 }}>
            {week?.tasks?.map((task, tIdx) => (
              <li key={tIdx} style={{ fontSize: 11, color: "#444", marginBottom: 3, lineHeight: 1.5 }}>{task}</li>
            ))}
          </ul>
        </div>
      ))}
      <Divider />
      {/* Section 5: Strategic Encouragement */}
      <SectionTitle icon="Compass" label="Section 5" title="Strategic Encouragement" />
      {pillars?.map((pillar, idx) => (
        <div key={idx} style={{ marginBottom: 8, paddingLeft: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{getIcon(pillar?.icon)}</span>
            <div>
              <strong style={{ fontSize: 12, color: "#1a1a2e" }}>{pillar?.title}</strong>
              <p style={{ margin: "2px 0 0 0", fontSize: 11, color: "#444", lineHeight: 1.5 }}>{pillar?.body}</p>
            </div>
          </div>
        </div>
      ))}
      <p style={{ fontSize: 12, fontStyle: "italic", color: "#555", marginTop: 10, paddingLeft: 4 }}>
        &ldquo;The goal is not to become a tech expert. The goal is to use technology as a tool — the same way you have used every other tool throughout your career.&rdquo;
      </p>
      <p style={{ fontSize: 11, color: "#888", paddingLeft: 4 }}>— QuantuMe Prospectus Strategic Principle</p>
      <Divider />
      {/* Section 6: First Three Steps */}
      <SectionTitle icon="ListChecks" label="Section 6" title="Your First Three Steps" />
      <p style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        Do these three things this week. Nothing else. Momentum begins with a single, concrete action.
      </p>
      {firstThreeSteps?.map((step, idx) => (
        <div key={idx} style={{ marginBottom: 10, paddingLeft: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: "#283ba3", flexShrink: 0 }}>{idx + 1}.</span>
            <div>
              <strong style={{ fontSize: 12, color: "#1a1a2e" }}>{step?.title}</strong>
              <p style={{ margin: "2px 0 0 0", fontSize: 11, color: "#444", lineHeight: 1.5 }}>{step?.description}</p>
              {step?.timeEstimate && (
                <p style={{ margin: "3px 0 0 0", fontSize: 10, color: "#888" }}>⏱ {step?.timeEstimate}</p>
              )}
            </div>
          </div>
        </div>
      ))}
      {/* Footer */}
      <Divider />
      <p style={{ fontSize: 10, color: "#aaa", textAlign: "center", marginTop: 8 }}>
        Generated by QuantuMe Prospectus · myprospectus.quantume.earth
      </p>
    </div>
  );
};

export default BlueprintPrintLayout;
